import { fmtUSD, fmtPct } from "@/lib/api";

export function Panel({ title, right, children, className = "", testId }) {
  return (
    <div className={`panel ${className}`} data-testid={testId}>
      {title && (
        <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-3 border-b border-[#222630]">
          <h3 className="text-sm font-semibold tracking-wide text-slate-200 uppercase">{title}</h3>
          {right}
        </div>
      )}
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  );
}

export function Metric({ label, value, sub, tone, testId, large }) {
  const toneCls = tone === "pos" ? "text-emerald-400" : tone === "neg" ? "text-red-400" : tone === "gold" ? "gold-text" : "text-slate-100";
  return (
    <div className="panel p-4 sm:p-5" data-testid={testId}>
      <div className="metric-label">{label}</div>
      <div className={`mt-2 font-mono font-bold tabular-nums ${large ? "text-3xl sm:text-4xl" : "text-xl sm:text-2xl"} ${toneCls}`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function Money({ value, tone, className = "" }) {
  const toneCls = tone === "pos" ? "text-emerald-400" : tone === "neg" ? "text-red-400" : "";
  return <span className={`font-mono tabular-nums ${toneCls} ${className}`}>{fmtUSD(value)}</span>;
}

export function Pct({ value, className = "" }) {
  const cls = value == null ? "" : value >= 0 ? "text-emerald-400" : "text-red-400";
  return <span className={`font-mono tabular-nums ${cls} ${className}`}>{fmtPct(value)}</span>;
}

const STATUS_STYLES = {
  PAPER: "bg-blue-500/10 text-blue-400 border-blue-500/40",
  LIVE_CONFIRM: "bg-amber-500/10 text-amber-400 border-amber-500/50",
  LIVE_AUTO: "bg-emerald-500/10 text-emerald-400 border-emerald-500",
  PROPOSED: "bg-slate-500/10 text-slate-300 border-slate-500/40",
  RISK_CHECK: "bg-blue-500/10 text-blue-400 border-blue-500/40",
  APPROVED: "bg-emerald-500/10 text-emerald-400 border-emerald-800/60",
  QUEUED: "bg-amber-500/10 text-amber-400 border-amber-800/60",
  SUBMITTED: "bg-amber-500/10 text-amber-400 border-amber-800/60",
  EXECUTED: "bg-emerald-500/10 text-emerald-400 border-emerald-800/60",
  RECONCILED: "bg-emerald-500/15 text-emerald-300 border-emerald-700/60",
  REJECTED: "bg-red-500/10 text-red-400 border-red-800/60",
  FAILED: "bg-red-500/10 text-red-400 border-red-800/60",
  CANCELED: "bg-slate-500/10 text-slate-400 border-slate-600/60",
  CORE: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/40",
  WATCHLIST: "bg-blue-500/10 text-blue-400 border-blue-800/60",
  BLOCKED: "bg-red-500/10 text-red-400 border-red-800/60",
  EXPERIMENTAL: "bg-purple-500/10 text-purple-400 border-purple-800/60",
  OPEN: "bg-amber-500/10 text-amber-400 border-amber-800/60",
  RECORDED: "bg-slate-500/10 text-slate-300 border-slate-600/60",
  CONNECTED: "bg-emerald-500/10 text-emerald-400 border-emerald-800/60",
  ACTIVE: "bg-emerald-500/10 text-emerald-400 border-emerald-800/60",
  DISCONNECTED: "bg-slate-500/10 text-slate-400 border-slate-600/60",
  CONFIGURED_UNVERIFIED: "bg-amber-500/10 text-amber-400 border-amber-800/60",
  NOT_IMPLEMENTED: "bg-slate-500/10 text-slate-400 border-slate-600/60",
  ERROR: "bg-red-500/10 text-red-400 border-red-800/60",
  STALE: "bg-amber-500/10 text-amber-400 border-amber-800/60",
};

export function StatusBadge({ status, testId }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.PROPOSED;
  return <span className={`badge ${cls}`} data-testid={testId}>{String(status || "—").replace("_", "-")}</span>;
}

export function Table({ head, children, testId }) {
  return (
    <div className="overflow-x-auto panel" data-testid={testId}>
      <table className="w-full border-collapse">
        <thead><tr>{head.map((h, i) => <th key={i} className={`th-cell ${h.num ? "text-right" : ""}`}>{h.label}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Empty({ children }) {
  return <div className="text-center text-slate-500 text-sm py-10">{children}</div>;
}
